import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { NamedLink } from '../../../../components';
import {
  bookingDayKeys,
  bookedChildFirstName,
  childColorFor,
} from '../../../BookingsPage/BookingsPage.helpers';

import css from './SectionWeekBookings.module.css';

const ALL_CHILDREN = 'all';
// How many distinct child avatars to show under a day before collapsing to "+N".
const MAX_MARKERS = 2;

const pad = n => String(n).padStart(2, '0');
// A wall-calendar day key from a date's own Y/M/D (matches bookingDayKey's YYYY-MM-DD shape).
const cellKey = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// Monday-first weekday index (0 = Mon .. 6 = Sun).
const mondayIndex = d => (d.getDay() + 6) % 7;
const addDays = (d, n) => {
  const next = new Date(d);
  next.setDate(d.getDate() + n);
  return next;
};
const startOfWeek = d => addDays(new Date(d.getFullYear(), d.getMonth(), d.getDate()), -mondayIndex(d));

const IconChevron = ({ dir }) => (
  <svg className={css.navIcon} viewBox="0 0 24 24" role="none">
    <path
      d={dir === 'prev' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * SectionWeekBookings -- the KidHive homepage "week view" of the parent's upcoming bookings (Figma
 * 0-8430). A compact card: the same All/per-child filter as the My Bookings page, a Monday-first week
 * strip that marks days with a booking using per-child colored dots (today highlighted), week prev/next
 * nav, and a "See All Bookings" link to the full page. Rendered only when the parent has upcoming
 * bookings (the parent decides who to show via the filter). Booking days are bucketed in each listing's
 * timezone, consistent with the My Bookings calendar.
 *
 * @param {Object} props
 * @param {Array} props.bookings the parent's upcoming booking transactions (denormalized)
 * @param {Array} props.children the parent's children ({ firstName, ... })
 * @param {Object} props.childColorMap firstName -> color (shared with the bookings page + calendar)
 */
const SectionWeekBookings = props => {
  const intl = useIntl();
  const { bookings = [], children = [], childColorMap = {} } = props;

  const [childFilter, setChildFilter] = useState(ALL_CHILDREN);
  const showChildFilter = children.length > 1;
  const activeChildFilter = showChildFilter ? childFilter : ALL_CHILDREN;

  // The Monday of the visible week; prev/next shift it by a week.
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));

  // Per day key: the distinct children (first name + color) with a booking that day, honoring the
  // active child filter. Plain colored dots on the strip come from this.
  const dayChildren = useMemo(() => {
    const map = {};
    bookings.forEach(tx => {
      const name = bookedChildFirstName(tx);
      if (activeChildFilter !== ALL_CHILDREN && name !== activeChildFilter) {
        return;
      }
      // Every occurrence day -- a term booking marks its whole series, not just the first session.
      bookingDayKeys(tx).forEach(key => {
        if (!map[key]) {
          map[key] = [];
        }
        if (!map[key].some(c => c.firstName === (name || ''))) {
          map[key].push({ firstName: name || '', color: childColorFor(name, childColorMap) });
        }
      });
    });
    return map;
  }, [bookings, activeChildFilter, childColorMap]);

  const days = Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i));
  const weekEnd = days[6];
  const todayKey = cellKey(new Date());

  const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
  const rangeLabel = sameMonth
    ? intl.formatDate(weekStart, { month: 'long', year: 'numeric' })
    : `${intl.formatDate(weekStart, { month: 'short' })} – ${intl.formatDate(weekEnd, {
        month: 'short',
        year: 'numeric',
      })}`;

  return (
    <section className={css.root}>
      <div className={css.inner}>
        <div className={css.header}>
          <h2 className={css.heading}>
            <FormattedMessage id="SectionWeekBookings.title" />
          </h2>
          <NamedLink name="BookingsPage" params={{ tab: 'upcoming' }} className={css.seeAll}>
            <FormattedMessage id="SectionWeekBookings.seeAll" />
          </NamedLink>
        </div>

        <div className={css.card}>
          {showChildFilter ? (
            <div className={css.filter} role="group" aria-label="Filter by child">
              <button
                type="button"
                className={classNames(css.childChip, {
                  [css.childChipActive]: activeChildFilter === ALL_CHILDREN,
                })}
                aria-pressed={activeChildFilter === ALL_CHILDREN}
                onClick={() => setChildFilter(ALL_CHILDREN)}
              >
                <FormattedMessage id="SectionWeekBookings.filterAll" />
              </button>
              {children.map(child => {
                const selected = activeChildFilter === child.firstName;
                return (
                  <button
                    key={child.id || child.firstName}
                    type="button"
                    className={classNames(css.childChip, { [css.childChipActive]: selected })}
                    aria-pressed={selected}
                    onClick={() => setChildFilter(child.firstName)}
                  >
                    <span
                      className={css.childChipInitial}
                      style={{ backgroundColor: childColorMap[child.firstName] }}
                    >
                      {child.firstName.charAt(0)}
                    </span>
                    {child.firstName}
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className={css.calHeader}>
            <span className={css.monthLabel}>{rangeLabel}</span>
            <div className={css.nav}>
              <button
                type="button"
                className={css.navButton}
                aria-label={intl.formatMessage({ id: 'SectionWeekBookings.prevWeek' })}
                onClick={() => setWeekStart(w => addDays(w, -7))}
              >
                <IconChevron dir="prev" />
              </button>
              <button
                type="button"
                className={css.navButton}
                aria-label={intl.formatMessage({ id: 'SectionWeekBookings.nextWeek' })}
                onClick={() => setWeekStart(w => addDays(w, 7))}
              >
                <IconChevron dir="next" />
              </button>
            </div>
          </div>

          <ol className={css.week}>
            {days.map(date => {
              const key = cellKey(date);
              const markers = dayChildren[key] || [];
              const isToday = key === todayKey;
              const shown = markers.slice(0, MAX_MARKERS);
              const overflow = markers.length - shown.length;
              return (
                <li key={key} className={css.day}>
                  <span className={css.dayLabel}>
                    {intl.formatDate(date, { weekday: 'short' })}
                  </span>
                  <span className={classNames(css.dayNumber, { [css.dayToday]: isToday })}>
                    {date.getDate()}
                  </span>
                  {markers.length > 0 ? (
                    <span className={css.markers} aria-hidden="true">
                      {shown.map((c, i) => (
                        <span
                          key={c.firstName || i}
                          className={css.marker}
                          style={{ backgroundColor: c.color }}
                        >
                          {c.firstName ? c.firstName.charAt(0) : ''}
                        </span>
                      ))}
                      {overflow > 0 ? <span className={css.markerMore}>+{overflow}</span> : null}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default SectionWeekBookings;
